import { useState } from "react";
import apiConvocatorias from "../../../api/Convocatorias/api-convocatorias";
import apiParticipantes from "../../../api/Participantes/api-participantes";
import apiMaestras from "../../../api/Maestras/api-maestras";
import getStorageUser from "../../../middlewares/GetStorageUser";
import { problemaDeEdad } from "../../../utils/edadParticipante";
import { useEncrypt } from "../../../hooks/useEncrypt";
import {
  Impedimento,
  impedimentoPorEstado,
  impedimentosParaInscribir,
} from "../../../utils/puedeInscribirse";

/**
 * El recuento previo a inscribir, y la inscripcion.
 *
 * Antes las reglas se comprobaban una por una al tocar el boton y la primera
 * que fallaba cortaba con un aviso, asi que habia que intentarlo tantas veces
 * como cosas faltaran. Aqui se juntan todas y se muestran de una vez antes de
 * inscribir.
 *
 * El estado de la convocatoria se pregunta al servidor al abrir el recuento,
 * porque pudo cambiar mientras la persona llenaba el formulario.
 */
export const useInscripcion = (convocatoria: any, postulacion: any, inscribir: () => any) => {
  const [abierto, setAbierto] = useState<boolean>(false);
  const [inscribiendo, setInscribiendo] = useState<boolean>(false);
  const [porEstado, setPorEstado] = useState<Impedimento | null>(null);
  const [porElServidor, setPorElServidor] = useState<Impedimento | null>(null);
  const [porLaEdad, setPorLaEdad] = useState<Impedimento | null>(null);
  const { decrypt } = useEncrypt();

  const consultarEstado = async () => {
    const id = convocatoria?.idconvocatorias;
    if (!id) return;
    try {
      const { data } = await apiConvocatorias.obtenerConvocatoriaConEstado(id);
      setPorEstado(impedimentoPorEstado(data));
    } catch (error) {
      // Si no se puede preguntar, no se inventa un impedimento: las demas
      // reglas siguen valiendo y el servidor rechaza igual lo que no procede.
      console.error("No se pudo consultar el estado de la convocatoria", error);
      setPorEstado(null);
    }
  };

  /**
   * Le pregunta al servidor si la inscripcion procede con esta categoria.
   * Es quien sabe, por ejemplo, si ya hay otra propuesta en la misma linea.
   */
  const verificarConElServidor = async () => {
    const idPostulacion = decrypt(localStorage.getItem("id_postulacion"));
    if (!idPostulacion || !convocatoria?.idconvocatorias) return;

    try {
      const respuesta: any = await apiParticipantes.verificarInscripcionPostulacion({
        // Sin el documento la consulta del servidor recibe undefined y revienta,
        // y su error llegaba aquí como un impedimento: "Proceso no se completo".
        documento: getStorageUser()?.documento,
        convocatoria_id: convocatoria.idconvocatorias,
        linea_convocatoria: convocatoria.linea_convocatoria,
        categoria_linea_convocatoria: postulacion?.categoria_linea_convocatoria,
        tipo_participante: localStorage.getItem("tipo_participante_postulacion"),
        id_postulacion: idPostulacion,
      });

      /*
        Solo se bloquea cuando el servidor dice que no, no cuando no pudo
        responder. Su respuesta de error no trae "ok", y tomarla por una
        negativa dejaba sin inscribir a quien lo tenía todo en regla, con un
        motivo que además no significaba nada para él.

        El motivo, cuando lo hay, lo redacta el servidor: aquí no se interpreta.
      */
      if (respuesta?.ok === false) {
        setPorElServidor({
          motivo:
            respuesta?.message ?? respuesta?.mensaje ?? "No es posible inscribir esta propuesta.",
        });
        return;
      }

      if (!respuesta?.ok) {
        console.warn("No se pudo verificar la inscripción; se sigue adelante.", respuesta);
      }
      setPorElServidor(null);
    } catch (error) {
      console.error("No se pudo verificar la inscripción", error);
      setPorElServidor(null);
    }
  };

  /**
   * Comprueba la edad con la fecha que haya guardada.
   *
   * Las reglas corren al escoger la fecha y al enviar el formulario, pero
   * una fecha que ya venia guardada y que nadie vuelve a tocar no pasaba
   * por ninguno de los dos. Aqui si.
   *
   * No aplica a la persona juridica: la regla es sobre la persona que se
   * presenta, y ese formulario no la tiene.
   */
  const comprobarLaEdad = async () => {
    if (Number(postulacion?.tipo_participante) === 2) return;

    const user = getStorageUser();
    if (!user?.documento) return;

    try {
      const [participante, lineas]: any[] = await Promise.all([
        apiParticipantes.consultarParticipante(user.documento),
        apiMaestras.obtenerLineaConvocatoria(),
      ]);

      const datos = participante?.data ?? participante;
      const suyo = Array.isArray(datos) ? datos[0] : datos;
      const nombreLinea =
        (Array.isArray(lineas) ? lineas : []).find(
          (x: any) => x?.id === convocatoria?.linea_convocatoria
        )?.nombre ?? "";

      const problema = problemaDeEdad(suyo?.fecha_nacimiento, {
        aceptaMenores: Boolean(convocatoria?.menor_edad),
        nombreLinea,
      });

      setPorLaEdad(problema ? { motivo: problema, paso: "perfil" } : null);
    } catch (error) {
      // Sin poder comprobarla no se inventa un impedimento: el formulario
      // ya la valida al escoger la fecha y al enviar.
      console.error("No se pudo comprobar la edad del participante", error);
      setPorLaEdad(null);
    }
  };

  const abrirRecuento = () => {
    setPorEstado(null);
    setPorElServidor(null);
    setPorLaEdad(null);
    setAbierto(true);
    consultarEstado();
    verificarConElServidor();
    comprobarLaEdad();
  };

  const cerrarRecuento = () => setAbierto(false);

  const impedimentos: Impedimento[] = [
    ...impedimentosParaInscribir(convocatoria, postulacion),
    ...(porEstado ? [porEstado] : []),
    ...(porElServidor ? [porElServidor] : []),
    ...(porLaEdad ? [porLaEdad] : []),
  ];

  const confirmar = async () => {
    // El boton va deshabilitado, pero la regla no puede depender de eso.
    if (impedimentos.length > 0) return;
    setInscribiendo(true);
    try {
      await inscribir();
      setAbierto(false);
    } finally {
      setInscribiendo(false);
    }
  };

  return {
    recuentoAbierto: abierto,
    abrirRecuento,
    cerrarRecuento,
    impedimentos,
    puedeInscribir: impedimentos.length === 0,
    inscribiendo,
    confirmar,
  };
};
